/**
 * @typedef {Object} TopBarButtonItem
 * @property {string} [icon] - Icon URL for the tab button.
 * @property {string} [imgColor] - CSS color value applied to the icon.
 * @property {JSX.Element | string} label - Text or JSX for the button label.
 * @property {string[]} requiredservice - List of services required to enable the tab.
 * @property {string} [path]  - Optional Navigation route triggered on click.
 * @property {Object} [state] - Optional navigation state passed to the route.
 * @property {string|number} [count] - Badge count shown on the right side. If "00", tab becomes disabled.
 * @property {boolean} [countLoading] - When true, renders a loader in place of the count while data is being fetched.
 */

/**
 * @typedef {Object} TopBarButtonsProps
 * @property {TopBarButtonItem[]} items - Array of tab/button objects to show in the top bar.
 * @property {string[]} services - Logged-in user's available services (used to enable/disable each tab).
 * @property {number} [rejectedTxnCount] - Optional count used to change the recreate-transactions icon dynamically.
 * @property {(function(): void)} [onClick] - Optional callback triggered on click. Runs before navigation if both are provided.
 */

/**
 * Component: TopBarButtons
 * Usage: Used to render dynamic action tabs on any dashboard.
 * Each tab can be enabled/disabled automatically based on services & count.
 *
 * @param {TopBarButtonsProps} props
 */

import { useNavigate } from "react-router-dom";
import SVGComponent from "../../SVGComponent";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import ICONS from "../../../../assets/Icons";

const TopBarButtons = ({
  items = [],
  services = [],
  onClick,
  itemsDisabled = false,
  inActiveVariant = false,
}) => {
  const navigate = useNavigate();

  const formatCount = (val) => {
    const num = Number(val) || 0;
    const digits = num.toString().length;

    if (digits > 4) return "9,999+";
    return num < 10 ? `0${num}` : num.toLocaleString("en-IN");
  };

  return (
    <div
      className={`w-width-full Qsmd:py-padding-sm py-padding-base bg-primary-nav flex flex-col Qsmd:flex-row rounded-border-radius-md items-center z-z-index-header ${itemsDisabled && inActiveVariant ? "bg-opacity-light-opacity" : ""}`}
    >
      <div
        className={`flex flex-col Qsmd:flex-row w-width-full  items-center Qsmd:gap-gap-base rounded-border-radius-md
      ${items.length === 1 ? "justify-center" : "Qsmd:justify-between"}`}
      >
        {items.map(
          (
            {
              icon,
              label,
              requiredservice = [],
              count,
              countLoading,
              path,
              state,
              imgColor,
              onClick: itemOnClick,
            },
            index,
            arr,
          ) => {
            const isServiceAvailable =
              Array.isArray(services) &&
              Array.isArray(requiredservice) &&
              requiredservice.every((service) => services.includes(service));

            const isDisabled =
              itemsDisabled ||
              (requiredservice?.length > GlobalConfiguration.lengthCheckZero &&
                !isServiceAvailable) ||
              countLoading ||
              count === GlobalConfiguration.lengthCheckZero ||
              count === GlobalConfiguration.dashboardPrimaryCount;

            const handleClick = () => {
              // tab disabled behaviour
              if (isDisabled) {
                if (requiredservice && !isServiceAvailable) {
                  navigate("/feature_not_assigned", {
                    state: { unavailableService: requiredservice },
                  });
                }
                return;
              }

              // ▶ item-level onClick (if developer passed)
              if (typeof itemOnClick === "function") {
                itemOnClick();
              }

              // ▶ component-level onClick (prop)
              if (typeof onClick === "function") {
                onClick();
              }

              // ▶ navigate only if path exists
              if (path) {
                navigate(path, { state });
              }
            };

            return (
              <div
                key={index}
                className={`w-width-full sm:flex-1 px-padding-base relative group hover:bg-none ${
                  items.length === 1 ? " flex justify-center" : ""
                }`}
              >
                <div
                  className={`flex items-center py-padding-sm px-padding-sm rounded-border-radius-md qlg:pl-padding-xs mx-margin-sm my-margin-sm Qsmd:my-margin-base ${
                    !isDisabled && isServiceAvailable
                      ? `cursor-pointer global-hover-white relative z-z-index-content px-padding-sm py-padding-sm ${
                          items.length === 1 ? "justify-center w-full" : ""
                        }`
                      : "opacity-light-opacity cursor-not-allowed"
                  }`}
                  onClick={handleClick}
                >
                  <div
                    className={`flex items-center gap-gap-sm ${
                      items.length === 1 ? "" : "w-full"
                    }`}
                    // onClick={handleClick}
                  >
                    {icon && (
                      <SVGComponent
                        color={imgColor}
                        height="var(--icon-md)"
                        width="var(--icon-md)"
                        url={icon}
                        alt="Icon"
                      />
                    )}

                    {/* label + count container */}
                    <div className="flex-1 flex flex-wrap items-start">
                      <span className="text-font-xs text-text-primary font-semibold break-words flex-1 min-w-0">
                        {label}
                      </span>

                      {/* COUNT - fixed place at right, never wraps down */}
                      {count !== undefined && (
                        <span className="text-font-xs font-semibold text-text-primary ml-margin-sm whitespace-nowrap">
                          {countLoading ? (
                            <img
                              src={ICONS?.LOADER_GIF}
                              alt="Loading..."
                              className="h-icon-sm inline-block"
                            />
                          ) : (
                            formatCount(count)
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Divider */}
                {index !== arr?.length - 1 && (
                  <div className="border-border Qsmd:border-r border-primary-bg Qsmd:w-auto Qsmd:pt-padding-xl Qsmd:absolute Qsmd:right-right-base Qsmd:top-1/2 transform Qsmd:-translate-y-1/2 mx-margin-sm Qsmd:mx-margin-base"></div>
                )}
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

export default TopBarButtons;
